import { useCallback, startTransition, type ReactElement } from 'react';
import { useModal, type ModalProps } from './useDialog';
import { type RegisterFormProps } from '../components/organisms/RegisterForm';
import { useAuthFormsContext } from '../contexts/AuthFormsContext';
import { useSkin } from '../contexts/SkinContext';
import { SKIN, type Skin } from '../types/enums';

const MODAL_PROPS_MAP: Record<Skin, ModalProps> = {
  [SKIN.BET365]: {
    anchor: 'bottom',
    maxWidth: 'xs',
    fullWidth: true,
    slotProps: {
      paper: {
        sx: {
          backgroundColor: 'background.paper',
          borderRadius: { xs: 0, md: 3 },
          width: '100%',
          margin: 0,
          maxHeight: '80vh',
        },
      },
    },
  },
  [SKIN.GOLA]: {
    anchor: 'bottom',
    maxWidth: 'xs',
    fullWidth: true,
    slotProps: {
      paper: {
        sx: {
          backgroundColor: 'background.paper',
          borderRadius: 0,
          width: '100%',
          height: {
            xs: 'calc(100dvh - var(--header-height, 0px))',
            md: 'auto',
          },
        },
      },
    },
  },
  [SKIN.NEOBET]: {
    anchor: 'bottom',
    maxWidth: 'xs',
    fullWidth: true,
    slotProps: {
      paper: {
        sx: {
          backgroundColor: 'background.paper',
          borderRadius: { xs: 0, md: 3 },
          width: '100%',
          height: {
            xs: 'calc(100dvh - var(--header-height, 0px))',
            md: 'auto',
          },
          maxHeight: { md: '80vh' },
        },
      },
    },
  },
};

export const getModalProps = (skin: Skin): ModalProps => MODAL_PROPS_MAP[skin];

const useAuthForms = () => {
  const { openModal } = useModal();
  const { LoginForm, RegisterForm, ForgotPasswordForm, CreateBOUsersForm } = useAuthFormsContext();
  const { skin } = useSkin();
  const commonModalProps = getModalProps(skin);

  const openFormModal = useCallback(
    (component: ReactElement) => {
      startTransition(() => {
        openModal(component, commonModalProps);
      });
    },
    [openModal, commonModalProps]
  );

  const openLoginForm = useCallback(() => {
    if (!LoginForm) return;
    openFormModal(<LoginForm />);
  }, [openFormModal, LoginForm]);

  const openRegisterForm = useCallback(
    (options?: RegisterFormProps) => {
      openFormModal(<RegisterForm {...options} />);
    },
    [openFormModal, RegisterForm]
  );

  const openForgotPasswordForm = useCallback(
    () => openFormModal(<ForgotPasswordForm />),
    [openFormModal, ForgotPasswordForm]
  );

  const openRegisterBOUserForm = useCallback(
    () => openFormModal(<CreateBOUsersForm />),
    [openFormModal, CreateBOUsersForm]
  );

  return {
    openLoginForm,
    openRegisterForm,
    openForgotPasswordForm,
    openRegisterBOUserForm,
  };
};

export default useAuthForms;
